import { useState, useMemo } from "react";
import { useSelector } from "react-redux";

import { Grid, Typography, Divider, CircularProgress } from "@mui/material";
import { AlternateEmail, PhoneAndroid } from "@mui/icons-material";

import { DateTime, Duration, Interval } from "luxon";

import { generateAvailableMonths } from "../../../../../utils/helpers";
import constants from "../../../../../utils/constants";
import BookingStep from "../BookingStep";
import Calendar from "./Calendar";
import TimeSlots from "./TimeSlots";

const now = DateTime.now();

const generateCalendarInfo = (branchInfo) => {
  const dayStart = DateTime.fromSQL(branchInfo.dayStart + " " + branchInfo.timeZone);
  const dayEnd = DateTime.fromSQL(branchInfo.dayEnd + " " + branchInfo.timeZone);
  const branchOpenInterval = Interval.fromDateTimes(dayStart, dayEnd).length("minutes");
  const slotsIntervalDuration = Duration.fromObject({ minutes: branchInfo.slotsInterval });
  const slotsAllocationDuration = Duration.fromObject({ minutes: branchInfo.slotsAllocation - 1, seconds: 59 });
  const minLeadTimeDuration = now.plus(Duration.fromObject({ hours: branchInfo.minLeadTime }));
  const maxLeadTimeDuration = now.plus(Duration.fromObject({ days: branchInfo.maxLeadTime }));

  const numerOfSlots = Math.floor((branchOpenInterval - branchInfo.slotsInterval) / branchInfo.slotsInterval);

  return {
    ...branchInfo,
    dayStart,
    dayEnd,
    branchOpenInterval,
    slotsIntervalDuration,
    slotsAllocationDuration,
    minLeadTimeDuration,
    maxLeadTimeDuration,
    numerOfSlots,
  };
};

export default function Scheduler(props) {
  const { headerHeight, footerHeight } = props;

  const branchesInfo = useSelector((state) => state.booking.branchesInfo);
  const clientDetails = useSelector((state) => state.booking.clientDetails);

  // const [availableTimes, setAvailableTimes] = useState({ am: true, pm: true }); ////Feature disabled by BAM's request
  const [selectedDateInfo, setSelectedDateInfo] = useState();

  const selectedBranch = clientDetails.selectedBranch;
  const branchInfo = branchesInfo[selectedBranch];
  const calendarInfo = useMemo(() => generateCalendarInfo(branchInfo), [branchInfo]);

  const dateChangeHandler = (selectedDateInfo) => {
    setSelectedDateInfo(selectedDateInfo);
  };

  const availableMonths = useMemo(() => generateAvailableMonths(branchInfo), [branchInfo]);

  const getInitialMonth = () => {
    return availableMonths.some((monthObject) => monthObject.month === now.month)
      ? now
      : Object.values(availableMonths)[0];
  };

  return (
    <BookingStep>
      <Grid container columnSpacing={3} rowSpacing={2} mb={2} justifyContent="center">
        <Grid item>
          {!branchInfo || !calendarInfo || !availableMonths ? (
            <Grid container justifyContent="center" alignItems="center" height="300px">
              <CircularProgress />
            </Grid>
          ) : (
            <Calendar
              onSetDateInfo={dateChangeHandler}
              initialMonth={getInitialMonth()}
              selectedBranch={selectedBranch}
              branchInfo={branchInfo}
              branchDayStart={calendarInfo.dayStart}
              minLeadDayIso={calendarInfo.minLeadTimeDuration.toISODate()}
              maxLeadDayIso={calendarInfo.maxLeadTimeDuration.toISODate()}
              selectedDateInfo={selectedDateInfo}
              availableMonths={availableMonths}
            />
          )}
        </Grid>

        <Grid item display={{ xs: "none", md: "block" }}>
          <Divider variant="middle" orientation="vertical" />
        </Grid>
        <Grid item display={{ xs: "block", md: "none" }} sx={{ width: "100%" }}>
          <Divider variant="middle" />
        </Grid>

        <Grid item maxHeight={`calc(100vh - 100px - ${headerHeight} - ${footerHeight})`}>
          {!calendarInfo ? (
            <Grid container justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Grid>
          ) : (
            <TimeSlots
              selectedDateInfo={selectedDateInfo}
              calendarInfo={calendarInfo}
              allocation={branchInfo.slotsAllocation}
            />
          )}
        </Grid>
      </Grid>

      <Grid container justifyContent="center" columnSpacing={3} rowSpacing={1}>
        <Grid item>
          <Typography variant="body2">If none of these dates/times work, please contact us:</Typography>
        </Grid>
        <Grid item>
          <Grid container alignItems="center">
            <AlternateEmail fontSize="inherit" color="primary" />
            <Typography variant="body2">
              &nbsp;<a href="mailto:inquiries@litco.ca">inquiries@litco.ca</a>
            </Typography>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container alignItems="center">
            <PhoneAndroid fontSize="inherit" color="primary" />
            <Typography variant="body2">
              &nbsp;<a href={"tel:+" + constants.LITCO_NUMBER}>{constants.LITCO_NUMBER}</a>
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </BookingStep>
  );
}
